import React from 'react'
import { QueryClient, QueryClientProvider } from 'react-query'
import { ReactQueryDevtools } from 'react-query-devtools'
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom'
import CssBaseline from '@material-ui/core/CssBaseline'
import { SnackbarProvider } from 'notistack'
import { NotificationProvider } from './common/notifications/NotificationContext'
import { SuperAdminRoute } from './common/route'

import Theme from './theme'
import GlobalStyleOverrides from './global-style-overrides'
import ErrorBoundary from './error-boundary'
import IdleChecker from './common/idle-checker/idle-checker'
import { CheckboxProvider } from './common/list-view/common/checkboxes/checkbox-context'

import Alerts from './pages/alerts/alerts'
import Disputes from './pages/disputes/disputes'
import Invoices from './pages/invoices/invoices'
import Transactions from './pages/transactions/transactions'
import Reports from './pages/reports/reports'
import Looker from './pages/reports/looker'

import SessionLogoutForm from './pages/sessions/SessionLogoutForm'
import SessionLoginForm from './pages/sessions/SessionLoginForm'
import SessionResetForm from './pages/sessions/SessionResetForm'
import SessionNewPasswordForm from './pages/sessions/SessionNewPasswordForm'

import CaidsRouter from './pages/caids/router'
import InquiryDefaultsRouter from './pages/inquiry-defaults/router'
import MidMerchantGroupsRouter from './pages/mid-merchant-groups/router'
import Users from './pages/users'
import EvidenceConfiguration from './pages/evidence-configuration/index'
import TemplateTooling from './pages/template-tooling'
import DisputeBotList from './pages/disputebot-list'
import DisputeBotAutomation from './pages/disputebot-automation'
import DETEditAdditions from './pages/DET-edit-additions'
import ResponsePreview from './pages/response-preview'
import CurrentUserProvider from './contexts/CurrentUserContext'
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext'
import ImageLibraryProvider from './pages/ImageLibrary/ImageLibraryProvider'

import { library } from '@fortawesome/fontawesome-svg-core'
import { fas } from '@fortawesome/free-solid-svg-icons'

const isProduction = process.env.NODE_ENV === 'production'

library.add(fas)

const queryClient = new QueryClient()

export const App = () => {
  return (
    <>
      <CurrentUserProvider>
        <FeatureFlagsProvider>
          <CssBaseline />
          <GlobalStyleOverrides />
          <QueryClientProvider client={queryClient}>
            <Theme>
              <ErrorBoundary>
                <CheckboxProvider>
                  <NotificationProvider>
                    <SnackbarProvider maxSnack={5}>
                      <BrowserRouter>
                        <Routes>
                          <Route
                            path="/disputes"
                            element={<Navigate to="/ui/disputes" />}
                          />
                          <Route path="/ui/disputes" element={<Disputes />} />
                          <Route path="/ui/alerts" element={<Alerts />} />
                          <Route path="/ui/invoices" element={<Invoices />} />
                          <Route
                            path="/ui/transactions"
                            element={<Transactions />}
                          />
                          <Route path="/ui/reports" element={<Reports />} />
                          <Route
                            path="/ui/looker/:report"
                            element={<Looker />}
                          />
                          <Route path="/users" element={<Users />} />
                          <Route
                            path="/ui/evidence_configuration/*"
                            element={<EvidenceConfiguration />}
                          />
                          <Route
                            path="/ui/logout"
                            element={<SessionLogoutForm />}
                          />
                          <Route
                            path="/ui/image-library"
                            element={<ImageLibraryProvider />}
                          />
                          <Route
                            path="/ui/login"
                            element={<SessionLoginForm />}
                          />
                          <Route
                            path="/ui/reset"
                            element={<SessionResetForm />}
                          />
                          <Route
                            path="/ui/new-password/:userId/:passwordResetToken"
                            element={<SessionNewPasswordForm />}
                          />
                          <Route
                            path="/ui/disputes/:id/response_preview"
                            element={<ResponsePreview />}
                          />
                          <Route
                            path="/ui/disputes/:id/det_edit"
                            element={<DETEditAdditions />}
                          />
                          <Route
                            path="/ui/template-tooling"
                            element={<TemplateTooling />}
                          />
                          <Route
                            path="/ui/disputebot"
                            element={
                              <SuperAdminRoute>
                                <DisputeBotList />
                              </SuperAdminRoute>
                            }
                          />
                          <Route
                            path="/ui/disputebot/automation"
                            element={
                              <SuperAdminRoute>
                                <DisputeBotAutomation />
                              </SuperAdminRoute>
                            }
                          />
                          <Route
                            path="/ui/disputebot/automation/:id"
                            element={
                              <SuperAdminRoute>
                                <DisputeBotAutomation />
                              </SuperAdminRoute>
                            }
                          />
                          {InquiryDefaultsRouter()}
                          {CaidsRouter()}
                          {MidMerchantGroupsRouter()}
                        </Routes>
                      </BrowserRouter>
                      <IdleChecker />
                    </SnackbarProvider>
                  </NotificationProvider>
                </CheckboxProvider>
              </ErrorBoundary>
            </Theme>
            {!isProduction && <ReactQueryDevtools />}
          </QueryClientProvider>
        </FeatureFlagsProvider>
      </CurrentUserProvider>
    </>
  )
}
